import { useState, useCallback } from "react";
import { API, apiCall } from "../api";
import { usePromo } from "../utils/promo";
import { AuthImage, AuthFileLink } from "./EvidenceFiles";

const RISK_STYLES = {
  bajo: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  medio: 'bg-amber-50 text-amber-800 border-amber-300',
  alto: 'bg-red-50 text-red-800 border-red-200',
};
const RISK_DOTS = { bajo: 'bg-emerald-500', medio: 'bg-amber-500', alto: 'bg-red-500' };

// Traffic-light risk label computed by the backend from the tenant's ratings
const RiskBadge = ({ risk, detailed = false }) => {
  if (!risk) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 border text-xs font-bold px-2.5 py-1 rounded-full ${RISK_STYLES[risk.level]}`}
      title="Calculado con el promedio de sus evaluaciones; el historial de pagos pesa más">
      <span className={`w-2 h-2 rounded-full ${RISK_DOTS[risk.level]}`} aria-hidden="true" />
      {risk.label}
      {detailed && risk.payment_average != null && <span className="font-medium">&middot; pagos {risk.payment_average}/5</span>}
    </span>
  );
};

const LandlordBadge = ({ verified }) => verified ? (
  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold px-2 py-0.5 rounded-full">
    <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clipRule="evenodd" /></svg>
    Propietario verificado
  </span>
) : (
  <span className="inline-flex items-center bg-stone-100 text-amber-800/80 border border-amber-200 text-xs font-medium px-2 py-0.5 rounded-full">Verificaci&oacute;n en proceso</span>
);

// Tenant Search Component
const TenantSearch = ({ token }) => {
  const [searchForm, setSearchForm] = useState({ name: '', email: '', phone: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [evidenceTenant, setEvidenceTenant] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [unlockingId, setUnlockingId] = useState(null);
  const promo = usePromo();

  const unlockReport = async (tenantId) => {
    setUnlockingId(tenantId);
    try {
      const checkout = await apiCall('/payments/unlock-report/session', 'POST', {
        tenant_id: tenantId,
        origin_url: window.location.origin
      }, token);
      window.location.href = checkout.url;
    } catch (error) {
      alert('No se pudo iniciar el pago: ' + (error.detail || error.message));
      setUnlockingId(null);
    }
  };

  // Search by name, email and/or phone
  const performSearch = useCallback(async () => {
    
    if (!searchForm.name && !searchForm.email && !searchForm.phone) {
      alert('Por favor proporciona al menos un par\u00e1metro de b\u00fasqueda');
      return;
    }

    setLoading(true);
    setHasSearched(false);
    setSearchResults([]);
    
    try {
      
      const response = await fetch(`${API}/tenants/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: searchForm.name.trim(),
          email: searchForm.email.trim().toLowerCase(),
          phone: searchForm.phone.trim()
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      setSearchResults(result || []);
      setHasSearched(true);
      
      
    } catch (error) {
      alert('Error en la b\u00fasqueda: ' + error.message);
      setSearchResults([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }, [searchForm, token]);

  const StarRating = ({ rating }) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating}/5)</span>
      </div>
    );
  };

  return (
    <div className="text-left">
      <h2 className="text-2xl font-extrabold tracking-tight text-amber-950">Busca a tu pr&oacute;ximo inquilino</h2>
      <p className="text-amber-900/70 mt-1 mb-6">Por nombre, correo o tel&eacute;fono. Te mostramos su historial y su sem&aacute;foro de riesgo.</p>
      
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
            <input
              type="text"
              value={searchForm.name}
              onChange={(e) => setSearchForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2.5 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="Juan P&eacute;rez"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Correo Electr&oacute;nico</label>
            <input
              type="email"
              value={searchForm.email}
              onChange={(e) => setSearchForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2.5 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="juan@correo.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tel&eacute;fono</label>
            <input
              type="tel"
              value={searchForm.phone}
              onChange={(e) => setSearchForm(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2.5 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="10 d&iacute;gitos"
            />
          </div>
        </div>
        
                <button
          type="button"
          disabled={loading}
          onClick={performSearch}
          className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-2.5 rounded-lg hover:from-amber-600 hover:to-amber-700 shadow-sm disabled:opacity-50 font-semibold"
        >
          {loading ? 'Buscando...' : 'Buscar'}
        </button>
      </div>

      {/* Search Results */}
      {hasSearched && searchResults.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="mx-auto w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-12 h-12 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-amber-800 mb-2">Inquilino no encontrado</h3>
          <p className="text-amber-600">No se encontraron registros con los criterios de b&uacute;squeda proporcionados.</p>
          <p className="text-amber-500 text-sm mt-2">Intenta con diferentes par&aacute;metros de b&uacute;squeda.</p>
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-lg font-bold text-amber-950 mb-4">Resultados</h3>
          {searchResults.map((tenant, index) => (
            <div key={index} className={`bg-white rounded-2xl border border-amber-100 border-l-4 ${tenant.risk ? {bajo: "border-l-emerald-500", medio: "border-l-amber-500", alto: "border-l-red-500"}[tenant.risk.level] : "border-l-amber-200"} p-4 sm:p-6 shadow-sm hover:shadow-lg hover:shadow-amber-100 transition-shadow`}>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-bold text-amber-950 mb-2 break-words">{tenant.tenant_name}</h4>
                  {tenant.locked ? (
                    <div className="text-sm text-gray-600">
                      <p className="font-medium text-amber-700">
                        {tenant.total_ratings} {tenant.total_ratings === 1 ? 'evaluaci\u00f3n' : 'evaluaciones'} registrada{tenant.total_ratings !== 1 ? 's' : ''}
                      </p>
                      {tenant.disputed_ratings > 0 && (
                        <p className="mt-1">{tenant.disputed_ratings} en disputa</p>
                      )}
                      <p className="mt-1">Desbloquea el reporte completo para ver calificaciones, comentarios y evidencia.</p>
                    </div>
                  ) : (
                  <>
                  <div className="space-y-1 text-sm text-gray-600 mb-3">
                    <p className="break-words"> {tenant.tenant_email}</p>
                    <p> {tenant.tenant_phone}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <StarRating rating={tenant.average_rating || 0} />
                    <span className="text-sm text-gray-500">
                      {tenant.total_ratings} {tenant.total_ratings === 1 ? 'evaluaci\u00f3n' : 'evaluaciones'}
                    </span>
                    <RiskBadge risk={tenant.risk} />
                    {tenant.detailed_ratings?.some(r => r.disputed) && <span className="inline-block bg-gray-100 text-gray-700 border border-gray-300 text-xs font-semibold px-2 py-0.5 rounded-full" title="La persona evaluada no está de acuerdo con esta evaluación">En disputa</span>}
                  </div>
                  </>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  {tenant.locked ? (
                  <button
                    onClick={() => unlockReport(tenant.id)}
                    disabled={unlockingId === tenant.id}
                    className="bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {unlockingId === tenant.id ? 'Redirigiendo...' : `Desbloquear reporte ($${promo.report_price_usd} USD)`}
                  </button>
                  ) : (
                  <>
                  <button
                    onClick={() => setSelectedTenant(tenant)}
                    className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-2 rounded-lg hover:from-amber-600 hover:to-amber-700 shadow-sm text-sm font-medium"
                  >
                    Ver Detalles
                  </button>
                  {tenant.detailed_ratings && tenant.detailed_ratings.some(r => r.evidence_files && r.evidence_files.length > 0) && (
                    <button
                      onClick={() => setEvidenceTenant(tenant)}
                      className="border border-amber-200 text-stone-800 px-4 py-2 rounded-lg hover:bg-amber-50 text-sm font-medium flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Ver Evidencia
                    </button>
                  )}
                  </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Evidence Modal */}
      {evidenceTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]" onClick={() => setEvidenceTenant(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full flex flex-col overflow-hidden" style={{ maxHeight: 'calc(100dvh - 2rem)' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center gap-3 p-4 sm:p-6 border-b bg-white flex-shrink-0">
              <h3 className="text-lg sm:text-xl font-semibold break-words min-w-0">Evidencia &mdash; {evidenceTenant.tenant_name}</h3>
              <button onClick={() => setEvidenceTenant(null)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none flex-shrink-0">&times;</button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
              {evidenceTenant.detailed_ratings && evidenceTenant.detailed_ratings.map((rating, idx) => {
                const files = rating.evidence_files || [];
                if (files.length === 0) return null;
                return (
                  <div key={idx} className="mb-6">
                    <p className="text-sm font-medium text-gray-500 mb-3">
                      Evaluaci&oacute;n del {new Date(rating.created_at).toLocaleDateString('es-ES')} &mdash; {files.length} archivo{files.length !== 1 ? 's' : ''}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {files.map((file, fileIdx) => (
                        file.content_type && file.content_type.includes('image') ? (
                          <AuthImage
                            key={fileIdx}
                            fileId={file.id}
                            fileUrl={file.url}
                            filename={file.filename}
                            token={token}
                            className="w-40 h-40 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity cursor-pointer shadow-sm"
                          />
                        ) : (
                          <AuthFileLink key={fileIdx} fileId={file.id} fileUrl={file.url} filename={file.filename} token={token} />
                        )
                      ))}
                    </div>
                  </div>
                );
              })}
              {evidenceTenant.detailed_ratings && !evidenceTenant.detailed_ratings.some(r => r.evidence_files && r.evidence_files.length > 0) && (
                <p className="text-gray-500 text-center py-8">Este inquilino no tiene archivos de evidencia.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tenant Details Modal */}
      {selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]" onClick={() => setSelectedTenant(null)}>
          <div className="bg-white rounded-lg max-w-4xl w-full flex flex-col overflow-hidden text-left" style={{ maxHeight: 'calc(100dvh - 2rem)' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center gap-3 p-4 sm:p-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg sm:text-xl font-semibold break-words min-w-0">{selectedTenant.tenant_name} - Calificaciones Detalladas</h3>
              <button
                onClick={() => setSelectedTenant(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none flex-shrink-0"
              >
                &times;
              </button>
            </div>
            <div className="space-y-4 p-4 sm:p-6 pt-4 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <p className="break-words"><strong>Email:</strong> {selectedTenant.tenant_email}</p>
                  <p><strong>Tel&eacute;fono:</strong> {selectedTenant.tenant_phone}</p>
                </div>
                <div>
                  <p><strong>Calificaci&oacute;n Promedio:</strong></p>
                  <StarRating rating={selectedTenant.average_rating || 0} />
                  <div className="mt-2"><RiskBadge risk={selectedTenant.risk} detailed /></div>
                </div>
              </div>

              {selectedTenant.detailed_ratings && selectedTenant.detailed_ratings.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Historial de Evaluaciones:</h4>
                  <div className="space-y-4">
                    {selectedTenant.detailed_ratings.map((rating, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4">
                        <div className="mb-3 flex flex-wrap gap-2">
                          <LandlordBadge verified={rating.landlord_verified} />
                        </div>
                        {rating.disputed && <div className="mb-3"><span className="inline-block bg-gray-100 text-gray-700 border border-gray-300 text-xs font-semibold px-2 py-0.5 rounded-full" title="La persona evaluada no está de acuerdo con esta evaluación">En disputa</span></div>}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3">
                          <div>
                            <span className="text-sm font-medium text-gray-700">Pago:</span>
                            <StarRating rating={rating.payment_reliability} />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Cuidado:</span>
                            <StarRating rating={rating.property_care} />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Comunicaci&oacute;n:</span>
                            <StarRating rating={rating.communication} />
                          </div>
                        </div>
                        <div className="mb-2">
                          <span className="text-sm font-medium text-gray-700">Calificaci&oacute;n General:</span>
                          <StarRating rating={rating.overall_rating} />
                        </div>
                        {rating.comments && (
                          <div className="mt-3">
                            <span className="text-sm font-medium text-gray-700">Comentarios:</span>
                            <p className="text-sm text-gray-600 mt-1">{rating.comments}</p>
                          </div>
                        )}
                        {rating.evidence_files && rating.evidence_files.length > 0 && (
                          <div className="mt-3">
                            <span className="text-sm font-medium text-gray-700">Evidencia ({rating.evidence_files.length} archivo{rating.evidence_files.length !== 1 ? 's' : ''}):</span>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {rating.evidence_files.map((file, fileIdx) => (
                                file.content_type && file.content_type.includes('image') ? (
                                  <AuthImage
                                    key={fileIdx}
                                    fileId={file.id}
                                    fileUrl={file.url}
                                    filename={file.filename}
                                    token={token}
                                    className="w-24 h-24 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity cursor-pointer"
                                  />
                                ) : (
                                  <AuthFileLink key={fileIdx} fileId={file.id} fileUrl={file.url} filename={file.filename} token={token} />
                                )
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="mt-2 text-xs text-gray-500">
                          Evaluado el: {new Date(rating.created_at).toLocaleDateString('es-ES')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { TenantSearch };
